import { formatPhoneNumber } from '../../../../../../utils';

interface IInformationSection1 {
  name: string;
  email: string;
  phone: string;
  clientType: string;
  dob: string;
  race: string;
}
interface IInformationSection2 {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  rentStatus: string;
  lengthInYears: string;
  codeViolation: string;
}

interface IInformationSection3 {
  propertyTaxes: string;
  waterBills: string;
  refuseBills: string;
  sewageBills: string;
}
interface IInformationSection4 {
  ownedProperties: any[];
}
interface IInformationSection5 {
  q1: string;
  projectFinancialReadiness: string;
  understandProcess: string;
}
interface IInformationSection6 {
  communitySupport: string;
  financialReadiness: string;
}

interface IClientInformationDetailsProps {
  step1: {
    section1: IInformationSection1;
    section2: IInformationSection2;
    section3: IInformationSection3;
    section4: IInformationSection4;
    section5: IInformationSection5;
    section6: IInformationSection6;
  };
}
export default function ClientInformationDetails({ step1 }: IClientInformationDetailsProps) {
  return (
    <section aria-labelledby='applicant-information-title'>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
            <h2 id='applicant-information-title' className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
              Client Information
            </h2>
          </div>
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6 flex flex-col gap-6'>
          <div className='space-y-2'>
            <h3 className='mt-1 max-w-2xl '>General Information</h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Name</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section1?.name}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Email</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section1?.email}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Phone</dt>
                <dd className='mt-1 text-sm text-gray-900'>+1 {formatPhoneNumber(step1?.section1?.phone)}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Date of Birth</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section1?.dob}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Race</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section1?.race}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Client Type</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section1?.clientType}</dd>
              </div>
            </dl>
          </div>
          <div className='space-y-2'>
            <h3 className='max-w-2xl '>Current Residence</h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Address</dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {step1?.section2?.streetAddress}, {step1?.section2?.city}, {step1?.section2?.state}, {step1?.section2?.zipCode}
                </dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>How long have you been at this address (in years) ?</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section2?.lengthInYears}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Own/Rent</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section2?.rentStatus}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Outstanding code violations or municipal liens on this property</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section2?.codeViolation}</dd>
              </div>
            </dl>
          </div>
          <div className='space-y-2'>
            <h3 className='mt-1 max-w-2xl '>Are the following up to date on this property ?</h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Property Taxes</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section3?.propertyTaxes}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Water Bills</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section3?.waterBills}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Sewage Bills</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section3?.sewageBills}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Refuse Bills</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section3?.refuseBills}</dd>
              </div>
            </dl>
          </div>
          <div className='space-y-2'>
            <h3 className='mt-1 max-w-2xl '>List of owned properties</h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2'>
              {step1?.section4.ownedProperties?.map((op, index) => (
                <div key={index} className='sm:col-span-1'>
                  <dt className='text-md font-semibold text-neutralColor-medium'>{op.propertyName}</dt>
                  <dd className='mt-1 text-sm text-gray-500 flex flex-col'>
                    <span>
                      {op.streetAddress}, {op.city}, {op.state}, {op.zipCode}
                    </span>
                    <span>Current Use: {op.currentUse}</span>
                    <span>Future Use: {op.futureUse}</span>
                    <span>Outstanding code violations/Municipal liens: {op.codeViolation}</span>
                    <span>Property Taxes: {op.propertyTaxes}</span>
                    <span>Water Bills: {op.propertyTaxes}</span>
                    <span>Sewage Bills: {op.propertyTaxes}</span>
                    <span>Refuse Bills: {op.propertyTaxes}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div className='space-y-2'>
            <h3 className='mt-1 max-w-2xl '>Experience with the system</h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Have you ever tried to buy land through the public process before ?</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section5.q1}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Project Financial Readiness Scale</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section5?.projectFinancialReadiness}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Process understanding scale</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section5.understandProcess}</dd>
              </div>
            </dl>
          </div>
          <div className='space-y-2'>
            <h3 className='mt-1 max-w-2xl '>Personal (Organizational) goals</h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Financial Readiness Rating</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section6.financialReadiness}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Community Support Rating</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step1?.section6.communitySupport}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}
